<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';

  import { GeneralDeleteForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const getVideos = async () => {
    const videos = await AssetsService.getAllVideos();
    return Array.isArray(videos)
      ? videos.map((video) => ({
          label: video.rulename,
          value: video.rulename,
          ...video
        }))
      : [];
  };

  const onDeleteVideo = async (video) => {
    const deleteResponse = await AssetsService.deleteVideo(video);

    if (deleteResponse?.status >= 400) {
      notify($format('label.FAILURE.DELETE', { values: { type: video.rulename } }), 'error');
      throw new Error($format('label.FAILURE.DELETE_VIDEO'));
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: video.rulename } }));

    step.data = {
      id: video.rulename,
      videoData: {
        rulename: video.rulename,
        url: video.url
      }
    };
  };

  const rollbackDeleteVideo = async () => {
    if (!step.data?.videoData) {
      return;
    }

    $appStore.loading = true;

    try {
      const response = await AssetsService.addVideo(step.data.videoData);

      if (response?.status >= 400) {
        throw new Error($format('label.FAILURE.ROLLBACK_VIDEO_DELETION'));
      }
    } catch (error) {
      console.error('Failed to rollback video deletion:', error);
    } finally {
      $appStore.loading = false;
    }
  };
</script>

<GeneralDeleteForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  rollbackFunction={rollbackDeleteVideo}
  getFunction={getVideos}
  deleteFunction={onDeleteVideo}
  showPreview={true}
>
  <div slot="preview" let:item>
    {#if item}
      <div class="video-preview">
        <div class="video-details">
          <div class="detail-item">
            <span class="label">Name:</span>
            <span class="value video-name">{item.rulename}</span>
          </div>
          <div class="detail-item">
            <span class="label">URL:</span>
            <span class="value video-url">{item.url}</span>
          </div>
        </div>
      </div>
    {/if}
  </div>
</GeneralDeleteForm>

<style lang="scss">
  .video-preview {
    background-color: var(--color-white);
    padding: var(--padding-base);

    .video-details {
      display: flex;
      flex-direction: column;
      gap: var(--margin-small);
      width: 100%;

      .detail-item {
        display: flex;
        gap: var(--margin-small);

        .label {
          color: var(--color-gray-600);
          flex-shrink: 0;
          font-weight: var(--font-weight-medium);
        }

        .value {
          color: var(--color-gray-900);
          word-break: break-all;

          &.video-name {
            font-weight: var(--font-weight-medium);
          }

          &.video-url {
            color: var(--color-blue-600);
          }
        }
      }
    }
  }
</style>
